import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { useMediaObjectUrl } from 'src/hooks/use-media-object-url';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

/**
 * Visor de un documento (PDF o imagen) servido por el BACKEND.
 *
 * Sustituye a los `data:application/pdf;base64,...` que se metian en un <iframe>: el
 * binario ya no viene dentro del JSON. Se descarga por el endpoint de streaming y se
 * muestra desde una object URL.
 *
 * Un 404 (documento inexistente, borrado, sin permiso o sin pertenencia) se resuelve con
 * un mensaje, no con un visor vacio.
 */
export default function MediaDocumentViewer({
  media,
  patientId,
  enabled = true,
  title = 'Documento',
  height = 600,
  sx,
}) {
  const { url, loading, error } = useMediaObjectUrl(media, {
    patientId,
    inline: true,
    enabled,
  });

  if (loading) {
    return <Skeleton variant="rectangular" sx={{ width: 1, height, borderRadius: 1, ...sx }} />;
  }

  if (error || !url) {
    return (
      <Stack
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 1,
          height,
          borderRadius: 1,
          color: 'text.disabled',
          bgcolor: 'background.neutral',
          ...sx,
        }}
      >
        <Iconify icon="eva:file-remove-outline" width={48} />
        <Typography variant="body2">Documento no disponible</Typography>
      </Stack>
    );
  }

  return (
    <Box
      component="iframe"
      title={title}
      src={url}
      sx={{ width: 1, height, border: 'none', borderRadius: 1, ...sx }}
    />
  );
}

MediaDocumentViewer.propTypes = {
  /** mediaId (string) o el objeto MediaBlob/MediaResponseDto devuelto por el backend. */
  media: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Paciente dueno del documento, cuando la vista esta en su contexto. */
  patientId: PropTypes.string,
  /** false para no descargar todavia (p. ej. mientras el dialogo esta cerrado). */
  enabled: PropTypes.bool,
  title: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
};
